/**
 * Add-to-bag button for bundle family products.
 *
 * The add itself is handled by the theme's product-form (selected variant and
 * quantity come from the variant picker / quantity selector). This component
 * only observes the successful add and decides where to send the customer:
 *   - recommendation already shown this session, or no candidates -> cart
 *   - otherwise -> /products/<handle>?view=<suffix>
 */
if (!customElements.get('bundle-add-to-bag')) {
  customElements.define(
    'bundle-add-to-bag',
    class BundleAddToBag extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.events =
          typeof PUB_SUB_EVENTS !== 'undefined'
            ? PUB_SUB_EVENTS
            : { cartUpdate: 'cart-update', variantChange: 'variant-change' };
        this.sectionId = this.dataset.sectionId || '';
        this.cartUrl = this.dataset.cartUrl || window.routes?.cart_url || '/cart';
        this.shownKey = this.dataset.shownKey || 'aura_bundle_recommendation_shown';
        this.recommendUrl = this.dataset.recommendUrl || '';
        this.recommendationIds = this.parseIds(this.dataset.recommendationIds);

        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.button = this.querySelector('[type="submit"]');
        this.label = this.button?.querySelector('[data-bundle-add-label]');
        this.buttonLabel = this.label?.textContent?.trim() || '';

        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(this.events.cartUpdate, this.onCartUpdate);
          this.variantChangeUnsubscriber = subscribe(this.events.variantChange, this.onVariantChange);
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onCartUpdate(event) {
        if (this.redirecting) return;
        if (event?.source !== 'product-form') return;

        const cartData = event.cartData;
        if (!cartData) return;

        // The section can host other product forms (e.g. quick add); only react
        // to an add of the variant currently selected in this form.
        const addedVariantId = String(event.productVariantId || '');
        const currentVariantId = String(this.variantInput?.value || '');
        if (addedVariantId && currentVariantId && addedVariantId !== currentVariantId) return;

        this.redirecting = true;
        const destination = this.destinationFor(cartData);
        this.trackAdd(cartData, addedVariantId, destination);
        window.location.assign(destination);
      }

      /**
       * product-info updates the submit button text to the theme's generic
       * "Add to cart"/"Sold out" strings after a variant change. Restore the
       * configured label when the variant is available.
       */
      onVariantChange(event) {
        const sectionId = event?.data?.sectionId;
        if (sectionId && this.sectionId && String(sectionId) !== String(this.sectionId)) return;
        if (!this.button || !this.label || !this.buttonLabel) return;
        if (this.button.hasAttribute('disabled')) return;

        this.label.textContent = this.buttonLabel;
      }

      /**
       * /cart/add.js returns the full cart only when `sections` is requested;
       * otherwise it returns just the affected line(s) — either as
       * `{ items: [...] }` or as a bare line item.
       */
      cartLines(cartData) {
        if (Array.isArray(cartData.items)) return cartData.items;
        if (cartData.variant_id != null || cartData.id != null) return [cartData];
        return [];
      }

      destinationFor(cartData) {
        if (!this.recommendUrl) return this.cartUrl;
        if (this.recommendationShown()) return this.cartUrl;

        const cartProductIds = new Set(
          this.cartLines(cartData)
            .map((item) => item.product_id)
            .filter((id) => id != null)
            .map(String)
        );
        const candidates = this.recommendationIds.filter((id) => !cartProductIds.has(id));

        return candidates.length > 0 ? this.recommendUrl : this.cartUrl;
      }

      recommendationShown() {
        try {
          return sessionStorage.getItem(this.shownKey) === '1';
        } catch (error) {
          // Storage unavailable: show the recommendation page rather than
          // silently skipping it.
          return false;
        }
      }

      parseIds(value) {
        return String(value || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }

      currentQuantity() {
        const input =
          document.getElementById(`Quantity-${this.sectionId}`) ||
          this.querySelector('input[name="quantity"]');
        const quantity = input ? parseInt(input.value, 10) : NaN;
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      trackAdd(cartData, variantId, destination) {
        const currency = window.Shopify?.currency?.active || 'USD';
        const line = this.cartLines(cartData).find(
          (item) => String(item.variant_id != null ? item.variant_id : item.id) === String(variantId)
        );

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'bundle_add_to_bag',
          variant_id: variantId || undefined,
          quantity: this.currentQuantity(),
          value: line ? line.final_price / 100 : undefined,
          currency,
          item_count: cartData.item_count,
          destination: destination === this.recommendUrl ? 'recommendation' : 'cart',
        });
      }
    }
  );
}
